import React, { useEffect, useState } from 'react';
import { organisationsAPI, checklistAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Organisation } from '../types';

const MATURITY_COLOUR: Record<string, string> = {
  L1: 'bg-red-100 text-red-700',
  L2: 'bg-orange-100 text-orange-700',
  L3: 'bg-yellow-100 text-yellow-700',
  L4: 'bg-blue-100 text-blue-700',
  L5: 'bg-green-100 text-green-700',
};

/** Shown to super_admin who has no org: a read-only grid of all organisations */
const AllOrgsView: React.FC = () => {
  const [orgs, setOrgs] = useState<Organisation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    organisationsAPI.list().then((res) => {
      setOrgs(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin h-10 w-10 border-b-2 border-blue-600 rounded-full" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">All Organisations</h1>
        <p className="text-sm text-gray-500 mt-1">{orgs.length} registered organisations on the platform</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {orgs.map((o) => (
          <div key={o.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-gray-900 text-sm">{o.name}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{o.industry} · {o.size || 'Size N/A'}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className={`px-2 py-0.5 rounded-full font-medium ${o.icc_formed ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                ICC {o.icc_formed ? '✓' : '✗'}
              </span>
              <span className={`px-2 py-0.5 rounded-full font-medium ${o.has_policy ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'}`}>
                Policy {o.has_policy ? '✓' : '✗'}
              </span>
              <span className={`px-2 py-0.5 rounded-full font-medium ${o.training_conducted ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-500'}`}>
                Training {o.training_conducted ? '✓' : '✗'}
              </span>
            </div>
            {o.posh_nodal_officer_name && (
              <p className="text-xs text-gray-500 mt-3 border-t border-gray-100 pt-2">
                <span className="font-medium">Nodal Officer:</span> {o.posh_nodal_officer_name}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const SIZES = ['<50', '50-250', '250-1000', '1000+'];
const TRAINING_FREQUENCIES = ['annual', 'biannual', 'quarterly'];

type Taxonomy = Record<string, Record<string, string[]>>;

const OrganisationPage: React.FC = () => {
  const { organisationId, user } = useAuth();
  const [org, setOrg] = useState<Organisation | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Partial<Organisation>>({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);

  // 4-dim taxonomy
  const [taxonomy, setTaxonomy] = useState<Taxonomy>({});
  const [workforceTypeOptions, setWorkforceTypeOptions] = useState<string[]>([]);
  const [workModeOptions, setWorkModeOptions] = useState<string[]>([]);

  // Derived cascading options
  const subIndustryOptions = form.industry ? Object.keys(taxonomy[form.industry] ?? {}) : [];
  const unitTypeOptions = (form.industry && form.sub_industry)
    ? (taxonomy[form.industry]?.[form.sub_industry] ?? [])
    : [];

  useEffect(() => {
    checklistAPI.taxonomy().then(res => {
      setTaxonomy(res.data.industry_taxonomy);
      setWorkforceTypeOptions(res.data.workforce_types);
      setWorkModeOptions(res.data.work_modes);
    });
    if (organisationId) {
      organisationsAPI.get(organisationId).then((res) => {
        setOrg(res.data);
        setForm(res.data);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [organisationId]);

  // Helper: toggle a tag in a string[] field
  const toggleTag = (field: 'workforce_types' | 'work_modes', val: string) => {
    const current: string[] = (form[field] as string[]) ?? [];
    setForm({
      ...form,
      [field]: current.includes(val) ? current.filter(x => x !== val) : [...current, val],
    });
  };

  const handleSave = async () => {
    if (!organisationId) return;
    setSaving(true);
    try {
      const res = await organisationsAPI.update(organisationId, form);
      setOrg(res.data);
      setEditing(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e) {
      alert('Failed to update organisation');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin h-10 w-10 border-b-2 border-blue-600 rounded-full" /></div>;

  if (!org && !organisationId) {
    // Super admin has no org — show platform-wide view
    if (user?.role === 'super_admin') return <AllOrgsView />;
    return (
      <div className="text-center py-16 text-gray-400">
        <span className="text-5xl">🏢</span>
        <p className="mt-3">No organisation linked to your account. Contact a Super Admin.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Organisation Profile</h1>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="px-4 py-2 border border-blue-500 text-blue-600 text-sm rounded-lg font-medium hover:bg-blue-50"
          >
            Edit Profile
          </button>
        )}
      </div>

      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          ✅ Organisation profile updated successfully
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
        {/* ── Tier-1/2/3 Industry + Size ── */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Organisation Name</label>
            {editing ? (
              <input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            ) : <p className="text-sm font-semibold text-gray-900">{org?.name}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Industry (Tier 1)</label>
            {editing ? (
              <select value={form.industry || ''} onChange={(e) => setForm({ ...form, industry: e.target.value, sub_industry: null, unit_type: null })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">Select industry…</option>
                {Object.keys(taxonomy).map(i => <option key={i}>{i}</option>)}
              </select>
            ) : <p className="text-sm text-gray-800">{org?.industry}</p>}
          </div>

          {/* Sub-industry (Tier 2) */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Sub-Industry (Tier 2)</label>
            {editing ? (
              subIndustryOptions.length > 0 ? (
                <select value={form.sub_industry || ''} onChange={(e) => setForm({ ...form, sub_industry: e.target.value, unit_type: null })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="">— Any / Not applicable —</option>
                  {subIndustryOptions.map(s => <option key={s}>{s}</option>)}
                </select>
              ) : <p className="text-xs text-gray-400 pt-2">Select industry first</p>
            ) : <p className="text-sm text-gray-800">{org?.sub_industry || '—'}</p>}
          </div>

          {/* Unit type (Tier 3) */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Unit / Ward / Dept (Tier 3)</label>
            {editing ? (
              unitTypeOptions.length > 0 ? (
                <select value={form.unit_type || ''} onChange={(e) => setForm({ ...form, unit_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="">— Any / Not applicable —</option>
                  {unitTypeOptions.map(u => <option key={u}>{u}</option>)}
                </select>
              ) : <p className="text-xs text-gray-400 pt-2">{form.sub_industry ? 'No sub-units defined' : 'Select sub-industry first'}</p>
            ) : <p className="text-sm text-gray-800">{org?.unit_type || '—'}</p>}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Organisation Size</label>
            {editing ? (
              <select value={form.size || ''} onChange={(e) => setForm({ ...form, size: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">Select…</option>
                {SIZES.map(s => <option key={s}>{s}</option>)}
              </select>
            ) : <p className="text-sm text-gray-800">{org?.size || '—'}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Training Frequency</label>
            {editing ? (
              <select value={form.training_frequency || ''} onChange={(e) => setForm({ ...form, training_frequency: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">Select…</option>
                {TRAINING_FREQUENCIES.map(f => <option key={f}>{f}</option>)}
              </select>
            ) : <p className="text-sm text-gray-800 capitalize">{org?.training_frequency || '—'}</p>}
          </div>
        </div>

        {/* ── Workforce Types ── */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-2">Workforce Types</label>
          {editing ? (
            <div className="flex flex-wrap gap-2">
              {workforceTypeOptions.map(wt => {
                const active = (form.workforce_types ?? []).includes(wt);
                return (
                  <button key={wt} type="button" onClick={() => toggleTag('workforce_types', wt)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      active ? 'bg-violet-600 text-white border-violet-600' : 'border-gray-300 text-gray-600 hover:border-violet-400'
                    }`}>
                    {wt}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {(org?.workforce_types ?? []).length > 0
                ? (org?.workforce_types ?? []).map(wt => (
                    <span key={wt} className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-xs">{wt}</span>
                  ))
                : <p className="text-sm text-gray-400">Not specified</p>
              }
            </div>
          )}
        </div>

        {/* ── Work Modes ── */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-2">Work Modes</label>
          {editing ? (
            <div className="flex flex-wrap gap-2">
              {workModeOptions.map(wm => {
                const active = (form.work_modes ?? []).includes(wm);
                return (
                  <button key={wm} type="button" onClick={() => toggleTag('work_modes', wm)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      active ? 'bg-teal-600 text-white border-teal-600' : 'border-gray-300 text-gray-600 hover:border-teal-400'
                    }`}>
                    {wm}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {(org?.work_modes ?? []).length > 0
                ? (org?.work_modes ?? []).map(wm => (
                    <span key={wm} className="px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 text-xs">{wm}</span>
                  ))
                : <p className="text-sm text-gray-400">Not specified</p>
              }
            </div>
          )}
        </div>

        {/* Booleans */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { key: 'has_policy', label: 'POSH Policy in Place' },
            { key: 'icc_formed', label: 'ICC Constituted' },
            { key: 'training_conducted', label: 'Training Conducted' },
          ].map(({ key, label }) => (
            <div key={key} className="flex items-center gap-2">
              {editing ? (
                <input type="checkbox" checked={!!(form as any)[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                  className="rounded" />
              ) : (
                <span className={(org as any)?.[key] ? 'text-green-600' : 'text-gray-300'}>
                  {(org as any)?.[key] ? '✅' : '☐'}
                </span>
              )}
              <span className="text-sm text-gray-700">{label}</span>
            </div>
          ))}
        </div>

        {/* Nodal officer */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">POSH Nodal Officer</label>
            {editing ? (
              <input value={form.posh_nodal_officer_name || ''} onChange={(e) => setForm({ ...form, posh_nodal_officer_name: e.target.value })}
                placeholder="Full name" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            ) : <p className="text-sm text-gray-800">{org?.posh_nodal_officer_name || '—'}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Nodal Officer Email</label>
            {editing ? (
              <input type="email" value={form.posh_nodal_officer_email || ''} onChange={(e) => setForm({ ...form, posh_nodal_officer_email: e.target.value })}
                placeholder="officer@company.com" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            ) : <p className="text-sm text-gray-800">{org?.posh_nodal_officer_email || '—'}</p>}
          </div>
        </div>

        {/* ICC Members preview */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-2">ICC Members</label>
          {org?.icc_members && org.icc_members.length > 0 ? (
            <div className="space-y-1">
              {org.icc_members.map((m, i) => (
                <div key={i} className="text-sm flex gap-2 text-gray-700">
                  <span className="font-medium">{m.name}</span>
                  <span className="text-gray-400">—</span>
                  <span>{m.role}</span>
                  <span className="text-gray-400">({m.type}, {m.gender})</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">No ICC members recorded.</p>
          )}
        </div>

        {editing && (
          <div className="flex gap-3 pt-2">
            <button onClick={() => setEditing(false)} className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 text-sm">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg font-semibold text-sm hover:bg-blue-700 disabled:opacity-60">
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganisationPage;
